<template>
  <div class="alarm-zone-indicator" :title="titleText">
    <!-- ── In alarm: red flashing crisis_alert + expanding ping ring ── -->
    <div v-if="alarmState === 'alarm'" class="azind-wrap">
      <div class="azind-ping"></div>
      <span class="material-symbols-outlined azind-icon azind-icon--alarm">crisis_alert</span>
    </div>

    <!-- ── Pending delay (armed + counting down): amber spinning arc + sensors ── -->
    <div v-else-if="alarmState === 'pending'" class="azind-wrap">
      <svg class="azind-arc-spin" viewBox="0 0 30 30" aria-hidden="true">
        <circle cx="15" cy="15" r="12" fill="none" stroke="rgba(245,158,11,0.2)" stroke-width="2.5" />
        <circle
          cx="15"
          cy="15"
          r="12"
          fill="none"
          stroke="rgb(245,158,11)"
          stroke-width="2.5"
          stroke-dasharray="25 50.5"
          stroke-linecap="round" />
      </svg>
      <span class="material-symbols-outlined azind-icon azind-icon--pending">sensors</span>
    </div>

    <!-- ── Armed: green shield with glow ── -->
    <div v-else-if="alarmState === 'armed'" class="azind-wrap">
      <span class="material-symbols-outlined azind-icon azind-icon--armed">shield</span>
    </div>

    <!-- ── Supervise: walking man while the sensor is active, teal eye otherwise ── -->
    <div v-else-if="alarmState === 'supervise'" class="azind-wrap">
      <div v-if="superviseDetected" class="motion-indicator" style="--motion-accent: rgb(20, 184, 166)">
        <span class="material-symbols-outlined motion-indicator__walk">directions_walk</span>
      </div>
      <span v-else class="material-symbols-outlined azind-icon azind-icon--supervise">visibility</span>
    </div>

    <!-- ── Disarmed: grey muted shield ── -->
    <div v-else class="azind-wrap">
      <span class="material-symbols-outlined azind-icon azind-icon--disarmed">shield_locked</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { tt } from '../../utils/consoleText';

interface Props {
  /** Alarm is actively armed (armed=true) */
  armed: boolean;
  /** Alarm has been triggered (alarm=true) */
  alarm: boolean;
  /** Pending delay countdown in progress (pending_trigger_at is set, while armed) */
  pending: boolean;
  /** Supervise mode: sends notifications on trigger but no full alarm */
  supervise: boolean;
  /** Supervising sensor is currently active — shows the walking man instead of the eye */
  sensorActive?: boolean;
}

const props = defineProps<Props>();

/** Priority: alarm > pending(armed+delay) > armed > supervise > disarmed */
const alarmState = computed<'alarm' | 'pending' | 'armed' | 'supervise' | 'disarmed'>(() => {
  if (props.alarm) return 'alarm';
  if (props.pending && props.armed) return 'pending';
  if (props.armed) return 'armed';
  if (props.supervise) return 'supervise';
  return 'disarmed';
});

/** Supervise mode + active sensor → walking man instead of the teal eye */
const superviseDetected = computed(() => props.supervise === true && props.sensorActive === true);

const titleText = computed(() => {
  switch (alarmState.value) {
    case 'alarm':
      return tt('ALARM TRIGGERED', 'ALARM SPROŽEN');
    case 'pending':
      return tt('Alarm pending – sensor triggered, counting down', 'Alarm v čakanju – senzor sprožen, odštevanje teče');
    case 'armed':
      return tt('Alarm armed', 'Alarm aktiviran');
    case 'supervise':
      return superviseDetected.value
        ? tt(
            'Supervise – sensor active, notifications sent on trigger',
            'Nadzor – senzor aktiven, obvestila ob sprožitvi',
          )
        : tt('Supervise – notifications sent on sensor trigger', 'Nadzor – obvestila ob sprožitvi senzorja');
    // `default` rather than `case 'disarmed'`: the template paints every state it does not recognise with its own v-else.
    default:
      return tt('Alarm disarmed', 'Alarm deaktiviran');
  }
});
</script>

<style scoped>
.alarm-zone-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

/* Common wrapper — 30×30 container for icon + any overlaid SVG/ring */
.azind-wrap {
  position: relative;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Base icon */
.azind-icon {
  font-size: 18px;
  line-height: 1;
  position: relative;
  z-index: 1;
}

/* ── Disarmed ────────────────────────────────────────────────────────── */
.azind-icon--disarmed {
  color: rgba(156, 163, 175, 0.6);
}

/* ── Armed ───────────────────────────────────────────────────────────── */
.azind-icon--armed {
  color: rgb(34, 197, 94);
  filter: drop-shadow(0 0 4px rgba(34, 197, 94, 0.55));
}

/* ── Pending (armed delay countdown) ──────────────────────────── */
.azind-icon--pending {
  color: rgb(245, 158, 11);
  font-size: 14px;
}

.azind-arc-spin {
  position: absolute;
  inset: 0;
  width: 30px;
  height: 30px;
  animation: azind-spin 1.1s linear infinite;
}

/* ── Armed ───────────────────────────────────────────────── */
.azind-icon--armed {
  color: rgb(34, 197, 94);
  filter: drop-shadow(0 0 4px rgba(34, 197, 94, 0.55));
}

/* ── Supervise ───────────────────────────────────────────── */
.azind-icon--supervise {
  color: rgb(20, 184, 166);
  filter: drop-shadow(0 0 4px rgba(20, 184, 166, 0.45));
  animation: azind-breathe 2.4s ease-in-out infinite;
}
.azind-icon--alarm {
  color: rgb(239, 68, 68);
  filter: drop-shadow(0 0 5px rgba(239, 68, 68, 0.9));
  animation: azind-flash 0.42s ease-in-out infinite alternate;
}

/* Expanding ring behind alarm icon */
.azind-ping {
  position: absolute;
  inset: 1px;
  border-radius: 50%;
  border: 2px solid rgb(239, 68, 68);
  animation: azind-ping 1s ease-out infinite;
}

/* ─── Keyframes ──────────────────────────────────────────────────────── */
@keyframes azind-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Both pulses move opacity only: the colour and its drop shadow are static, so
   the browser composites the frame instead of repainting a blurred glyph. */
@keyframes azind-flash {
  from {
    opacity: 1;
  }
  to {
    opacity: 0.45;
  }
}

@keyframes azind-ping {
  0% {
    transform: scale(0.7);
    opacity: 0.85;
  }
  70% {
    transform: scale(1.5);
    opacity: 0.25;
  }
  100% {
    transform: scale(1.7);
    opacity: 0;
  }
}

@keyframes azind-breathe {
  0%,
  100% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .azind-icon--supervise,
  .azind-icon--alarm,
  .azind-arc-spin,
  .azind-ping {
    animation: none;
  }
  .azind-ping {
    opacity: 0;
  }
}
</style>
